"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  Textarea,
  Select,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  AlertDescription,
  VStack,
  HStack,
  Text,
  Checkbox,
  Box,
  FormHelperText,
  Badge,
  Tooltip,
  IconButton,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  Icon,
  AlertTitle,
} from "@chakra-ui/react";
import { ChevronDown, Check, CheckCircle2, Plus, X, Info } from "lucide-react";
import {
  smsPhoneNumberService,
  A2P_USE_CASES,
  type A2PCampaignInfo,
  type SMSPhoneNumber,
} from "../../sms-services/smsPhoneNumberService";

interface A2PRegistrationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: SMSPhoneNumber;
  onComplete: () => void;
}

const USE_CASE_INFO: Record<string, { price: number; description: string }> = {
  LOW_VOLUME: {
    price: 1.5,
    description:
      "The Low Volume Mixed Campaign use case can be used to send messages of multiple use cases such as Customer Care and Delivery Notification, but it is on the lowest throughput tier regardless of the Brand's Trust Score. Due to the lower cost, it is recommended for customers with the Low Volume Standard Brand, as well as Standard Brand customers who do not need high throughput.",
  },
  "2FA": {
    price: 10,
    description: "Any authentication or account verification such as OTP.",
  },
  ACCOUNT_NOTIFICATION: {
    price: 10,
    description:
      "Notifications about the status of an account or related to being a part of an account.",
  },
  CUSTOMER_CARE: {
    price: 10,
    description:
      "Support, account management, and other avenues of customer interaction.",
  },
  DELIVERY_NOTIFICATION: {
    price: 10,
    description: "Information about the status of a delivery.",
  },
  FRAUD_ALERT: {
    price: 10,
    description:
      "Messaging about potential fraudulent activity such as spending alerts.",
  },
  HIGHER_EDUCATION: {
    price: 10,
    description:
      "Message campaigns from colleges, universities, and other education institutions.",
  },
  MARKETING: {
    price: 10,
    description: "Promotional content such as sales and limited-time offers.",
  },
  MIXED: {
    price: 10,
    description:
      "A campaign that covers multiple use cases such as Customer Care and Delivery Notifications. Note: Mixed campaigns are likely to have lower throughput and a higher cost per message.",
  },
  POLLING_VOTING: {
    price: 10,
    description:
      "For conducting polling and voting, such as customer surveys. Not for political use.",
  },
  PUBLIC_SERVICE_ANNOUNCEMENT: {
    price: 10,
    description: "PSAs to raise audience awareness about a given topic.",
  },
  SECURITY_ALERT: {
    price: 10,
    description:
      "Notification of a compromised system (software or hardware related).",
  },
  AGENTS_FRANCHISES: {
    price: 30,
    description:
      "Brands that have multiple agents, franchises, or offices in the same brand vertical, but require individual localized numbers per agent/location/office. Note: Franchises/businesses with a separate EIN should register as a separate brand. Maximum 5000 numbers per campaign.",
  },
  EMERGENCY: {
    price: 5,
    description:
      "Notification services designed to support public safety/health during natural disasters, armed conflicts, pandemics, and other national or regional emergencies.",
  },
  K12_EDUCATION: {
    price: 10,
    description:
      "For messaging platforms that support schools from grades K-12 and distance learning centers.",
  },
  PROXY: {
    price: 10,
    description:
      "Peer-to-peer app-based group messaging with proxy/pooled numbers. Supports personalized services and non-exposure of personal numbers for enterprise or A2P communications. A phone number is assigned to a conversation (e.g., end-user/agent pair), rather than a single business or individual. Both parties send messages through that proxy number, which relays and protects user privacy.",
  },
  SOCIAL: {
    price: 10,
    description:
      "Communication between public figures/influencers and their communities.",
  },
  SWEEPSTAKE: {
    price: 10,
    description: "All sweepstakes-related messaging.",
  },
};

type UseCaseOpt = { value: string; label: string };
type UseCaseInfo = { price?: number; description?: string } | undefined;

function UseCaseDropdown({
  value,
  onChange,
  options,
  getInfo,
  invalid = false,
  maxDescriptionLines = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  options: UseCaseOpt[]; // e.g. A2P_USE_CASES
  getInfo: (v: string) => UseCaseInfo; // v -> USE_CASE_INFO[v]
  invalid?: boolean;
  maxDescriptionLines?: number;
}) {
  const selected = options.find((o) => o.value === value);
  const selectedInfo = getInfo(value);

  return (
    <Menu matchWidth>
      {/* Selected view: ONLY name + price */}
      <MenuButton
        as={Button}
        variant="outline"
        rightIcon={<ChevronDown size={16} />}
        justifyContent="space-between"
        w="full"
        textAlign="left"
        aria-invalid={invalid || undefined}
        borderColor={invalid ? "red.300" : undefined}
        _hover={{ borderColor: invalid ? "red.400" : undefined }}
        _focusVisible={{
          boxShadow: invalid
            ? "0 0 0 1px var(--chakra-colors-red-400)"
            : undefined,
        }}
      >
        <Flex direction="row" align="center" justify="space-between" w="full">
          <Text fontWeight="medium" noOfLines={1}>
            {selected?.label ?? "Select a use case"}
          </Text>
          {selectedInfo?.price != null && (
            <Text ml={2} color="gray.600" fontWeight="normal" noOfLines={1}>
              ${selectedInfo.price.toFixed(2)}/mo
            </Text>
          )}
        </Flex>
      </MenuButton>

      {/* Dropdown options: name + price (line 1), description (line 2) */}
      <MenuList maxH="320px" overflowY="auto" zIndex={10}>
        {options.map((opt) => {
          const i = getInfo(opt.value);
          const isSelected = opt.value === value;
          return (
            <MenuItem
              key={opt.value}
              onClick={() => onChange(opt.value)}
              whiteSpace="normal"
              py={3}
            >
              <HStack align="start" spacing={3} w="full">
                <Box flex="1">
                  <Text fontWeight="semibold">
                    {opt.label}
                    {i?.price != null && (
                      <Text as="span" color="gray.600" fontWeight="normal">
                        {" "}
                        — ${i.price.toFixed(2)}/mo
                      </Text>
                    )}
                  </Text>
                  {i?.description && (
                    <Text
                      fontSize="sm"
                      color="gray.600"
                      mt={1}
                      noOfLines={maxDescriptionLines}
                    >
                      {i.description}
                    </Text>
                  )}
                </Box>
                {isSelected && (
                  <Icon as={Check} boxSize={4} color="green.500" mt={1} />
                )}
              </HStack>
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
}

export function A2PRegistrationDialog({
  isOpen,
  onClose,
  phoneNumber,
  onComplete,
}: A2PRegistrationDialogProps) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [campaignInfo, setCampaignInfo] = useState<A2PCampaignInfo>({
    brandRegistrationSid: process.env.REACT_APP_TWILIO_BRAND_SID || "",
    usAppToPersonUsecase:
      (phoneNumber.a2p_campaign_use_case as A2PCampaignInfo["usAppToPersonUsecase"]) ||
      "LOW_VOLUME",
    description: phoneNumber.a2p_campaign_description || "",
    messageSamples:
      phoneNumber.a2p_campaign_message_samples &&
      Array.isArray(phoneNumber.a2p_campaign_message_samples)
        ? phoneNumber.a2p_campaign_message_samples
        : ["", ""],
    messageFlow: phoneNumber.a2p_campaign_message_flow || "",
    hasEmbeddedLinks: phoneNumber.a2p_campaign_has_embedded_links || false,
    hasEmbeddedPhone: phoneNumber.a2p_campaign_has_embedded_phone || false,
    directLending: phoneNumber.a2p_direct_lending || false,
    ageGated: phoneNumber.a2p_age_gated || false,
    optInKeywords: phoneNumber.a2p_campaign_opt_in_keywords || "",
    optInMessage: phoneNumber.a2p_campaign_opt_in_message || "",
    optOutMessage: phoneNumber.a2p_campaign_opt_out_message || "",
    helpMessage: phoneNumber.a2p_campaign_help_message || "",
  });

  useEffect(() => {
    if (isOpen && phoneNumber) {
      setCampaignInfo({
        brandRegistrationSid: process.env.REACT_APP_TWILIO_BRAND_SID || "",
        usAppToPersonUsecase:
          (phoneNumber.a2p_campaign_use_case as A2PCampaignInfo["usAppToPersonUsecase"]) ||
          "LOW_VOLUME",
        description: phoneNumber.a2p_campaign_description || "",
        messageSamples:
          phoneNumber.a2p_campaign_message_samples &&
          Array.isArray(phoneNumber.a2p_campaign_message_samples)
            ? phoneNumber.a2p_campaign_message_samples
            : ["", ""],
        messageFlow: phoneNumber.a2p_campaign_message_flow || "",
        hasEmbeddedLinks: phoneNumber.a2p_campaign_has_embedded_links || false,
        hasEmbeddedPhone: phoneNumber.a2p_campaign_has_embedded_phone || false,
        directLending: phoneNumber.a2p_direct_lending || false,
        ageGated: phoneNumber.a2p_age_gated || false,
        optInKeywords: phoneNumber.a2p_campaign_opt_in_keywords || "",
        optInMessage: phoneNumber.a2p_campaign_opt_in_message || "",
        optOutMessage: phoneNumber.a2p_campaign_opt_out_message || "",
        helpMessage: phoneNumber.a2p_campaign_help_message || "",
      });
    }
  }, [isOpen, phoneNumber]);

  const handleSubmit = async () => {
    // Validate required fields
    if (
      campaignInfo.description.length < 40 ||
      campaignInfo.description.length > 4096
    ) {
      setError("Campaign description must be between 40 and 4096 characters");
      return;
    }

    if (
      campaignInfo.messageFlow.length < 40 ||
      campaignInfo.messageFlow.length > 2048
    ) {
      setError("Message flow must be between 40 and 2048 characters");
      return;
    }

    if (
      campaignInfo.messageSamples.length < 2 ||
      campaignInfo.messageSamples.length > 5
    ) {
      setError("You must provide between 2 and 5 message samples");
      return;
    }

    for (const sample of campaignInfo.messageSamples) {
      if (sample.length < 20 || sample.length > 1024) {
        setError("Each message sample must be between 20 and 1024 characters");
        return;
      }
    }

    // Validate opt-in message if keywords provided
    const hasKeywords =
      campaignInfo.optInKeywords &&
      campaignInfo.optInKeywords.trim().length > 0;
    if (hasKeywords) {
      if (
        !campaignInfo.optInMessage ||
        campaignInfo.optInMessage.trim().length < 20
      ) {
        setError(
          "Opt-in message is required (20-320 chars) when opt-in keywords are provided"
        );
        return;
      }
      if (campaignInfo.optInMessage.length > 320) {
        setError("Opt-in message must not exceed 320 characters");
        return;
      }
    }

    setLoading(true);
    setSubmitting(true);
    setError(null);

    try {
      await smsPhoneNumberService.saveCampaignDetails(
        phoneNumber.id,
        campaignInfo
      );
      await smsPhoneNumberService.submitA2P(phoneNumber.id);
      setSuccess(true);
      setTimeout(() => {
        onComplete();
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to submit A2P registration"
      );
      setSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  const addMessageSample = () => {
    if (campaignInfo.messageSamples.length < 5) {
      setCampaignInfo({
        ...campaignInfo,
        messageSamples: [...campaignInfo.messageSamples, ""],
      });
    }
  };

  const removeMessageSample = (index: number) => {
    if (campaignInfo.messageSamples.length > 2) {
      setCampaignInfo({
        ...campaignInfo,
        messageSamples: campaignInfo.messageSamples.filter(
          (_, i) => i !== index
        ),
      });
    }
  };

  const updateMessageSample = (index: number, value: string) => {
    const newSamples = [...campaignInfo.messageSamples];
    newSamples[index] = value;
    setCampaignInfo({ ...campaignInfo, messageSamples: newSamples });
  };

  const selectedUseCaseInfo = USE_CASE_INFO[campaignInfo.usAppToPersonUsecase];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>
          {phoneNumber.a2p_registration_status === "rejected"
            ? "Resubmit A2P 10DLC Campaign Registration"
            : "A2P 10DLC Campaign Registration"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Register your phone number for A2P 10DLC messaging compliance (US
            numbers only)
          </Text>

          {phoneNumber.a2p_registration_status === "rejected" &&
            phoneNumber.a2p_registration_error_details && (
              <Alert status="error" mb={4} alignItems="start">
                <AlertIcon />
                <Box flex="1">
                  <AlertTitle>Previous Registration was Rejected</AlertTitle>
                  <AlertDescription>
                    {phoneNumber.a2p_registration_error_details?.errors &&
                      Array.isArray(
                        phoneNumber.a2p_registration_error_details.errors
                      ) && (
                        <VStack align="stretch" mt={2} spacing={1}>
                          {phoneNumber.a2p_registration_error_details.errors.map(
                            (error: any, idx: number) => (
                              <Box
                                key={idx}
                                fontSize="sm"
                                pl={2}
                                borderLeft="2px solid"
                                borderColor="red.300"
                              >
                                {error.error_code && (
                                  <Text
                                    as="span"
                                    fontWeight="bold"
                                    color="red.700"
                                  >
                                    [{error.error_code}]
                                  </Text>
                                )}{" "}
                                {error.failure_reason ||
                                  error.description ||
                                  "Unknown error"}
                              </Box>
                            )
                          )}
                        </VStack>
                      )}
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      Please review and correct the information below before
                      resubmitting.
                    </Text>
                  </AlertDescription>
                </Box>
              </Alert>
            )}

          {error && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success ? (
            <VStack spacing={4} py={8}>
              <Box color="green.500">
                <CheckCircle2 size={48} />
              </Box>
              <Text fontSize="lg" fontWeight="medium">
                Campaign Registration Submitted!
              </Text>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Your A2P campaign registration has been submitted. You'll
                receive updates on the status via email and in the phone numbers
                dashboard.
              </Text>
            </VStack>
          ) : submitting ? (
            <VStack spacing={4} py={8}>
              <Spinner size="xl" color="primary.500" />
              <Text fontSize="lg" fontWeight="medium">
                Submitting A2P Campaign Registration...
              </Text>
              <Text fontSize="sm" color="gray.500">
                This may take a moment
              </Text>
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              <FormControl
                isRequired
                isInvalid={!campaignInfo.usAppToPersonUsecase}
              >
                <FormLabel>Available A2P Campaign Use Cases</FormLabel>
                <UseCaseDropdown
                  value={campaignInfo.usAppToPersonUsecase}
                  onChange={(v) =>
                    setCampaignInfo({
                      ...campaignInfo,
                      usAppToPersonUsecase:
                        v as A2PCampaignInfo["usAppToPersonUsecase"],
                    })
                  }
                  options={A2P_USE_CASES} // [{ value, label }]
                  getInfo={(v) =>
                    USE_CASE_INFO[v as keyof typeof USE_CASE_INFO]
                  }
                  invalid={!campaignInfo.usAppToPersonUsecase}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Campaign Description</FormLabel>
                <Textarea
                  value={campaignInfo.description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setCampaignInfo({
                      ...campaignInfo,
                      description: e.target.value,
                    })
                  }
                  placeholder="Example: This campaign sends one-time passcodes to the end users when they try to log into our company's website."
                  rows={4}
                  resize="vertical"
                />
                <FormHelperText>
                  {campaignInfo.description.length} / 4096 characters (minimum
                  40)
                </FormHelperText>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>
                  Sample Messages (2-5 required)
                  <Tooltip label="Provide examples of messages that you will be sending with this campaign">
                    <Box as="span" ml={1} cursor="help">
                      <Info size={14} style={{ display: "inline" }} />
                    </Box>
                  </Tooltip>
                </FormLabel>
                <VStack spacing={2} align="stretch">
                  {campaignInfo.messageSamples.map((sample, index) => (
                    <HStack key={index} align="start">
                      <Box flex={1}>
                        <Textarea
                          value={sample}
                          onChange={(
                            e: React.ChangeEvent<HTMLTextAreaElement>
                          ) => updateMessageSample(index, e.target.value)}
                          placeholder="Example: Your one-time passcode is 123456."
                          rows={2}
                          resize="vertical"
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          {sample.length} / 1024 characters (minimum 20)
                        </Text>
                      </Box>
                      {campaignInfo.messageSamples.length > 2 && (
                        <IconButton
                          aria-label="Remove sample"
                          icon={<X size={16} />}
                          size="sm"
                          variant="outline"
                          colorScheme="red"
                          onClick={() => removeMessageSample(index)}
                        />
                      )}
                    </HStack>
                  ))}
                  {campaignInfo.messageSamples.length < 5 && (
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<Plus size={16} />}
                      onClick={addMessageSample}
                      alignSelf="start"
                    >
                      Add Sample ({campaignInfo.messageSamples.length} of 5)
                    </Button>
                  )}
                </VStack>
              </FormControl>

              <FormControl>
                <FormLabel>Message Content</FormLabel>
                <VStack align="start" spacing={2}>
                  <Checkbox
                    isChecked={campaignInfo.hasEmbeddedLinks}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setCampaignInfo({
                        ...campaignInfo,
                        hasEmbeddedLinks: e.target.checked,
                      })
                    }
                  >
                    Messages will include embedded links
                  </Checkbox>
                  <Checkbox
                    isChecked={campaignInfo.hasEmbeddedPhone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setCampaignInfo({
                        ...campaignInfo,
                        hasEmbeddedPhone: e.target.checked,
                      })
                    }
                  >
                    Messages will include phone numbers
                  </Checkbox>
                  <Checkbox
                    isChecked={campaignInfo.directLending}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setCampaignInfo({
                        ...campaignInfo,
                        directLending: e.target.checked,
                      })
                    }
                  >
                    Messages include content related to direct lending or other
                    loan arrangement
                  </Checkbox>
                  <Checkbox
                    isChecked={campaignInfo.ageGated}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setCampaignInfo({
                        ...campaignInfo,
                        ageGated: e.target.checked,
                      })
                    }
                  >
                    Messages include age-gated content as defined by Carrier and
                    CTIA guidelines
                  </Checkbox>
                </VStack>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>
                  How do end-users consent to receive messages?
                  <Tooltip label="Describe how end users opt-in to the campaign">
                    <Box as="span" ml={1} cursor="help">
                      <Info size={14} style={{ display: "inline" }} />
                    </Box>
                  </Tooltip>
                </FormLabel>
                <Textarea
                  value={campaignInfo.messageFlow}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setCampaignInfo({
                      ...campaignInfo,
                      messageFlow: e.target.value,
                    })
                  }
                  placeholder="Example: End users opt-in by visiting www.examplewebsite.com and adding their phone number..."
                  rows={5}
                  resize="vertical"
                />
                <FormHelperText>
                  {campaignInfo.messageFlow.length} / 2048 characters (minimum
                  40)
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>
                  Opt-in Keywords (optional)
                  <Tooltip label="Keywords users can text to opt-in (e.g., 'Subscribe', 'Start')">
                    <Box as="span" ml={1} cursor="help">
                      <Info size={14} style={{ display: "inline" }} />
                    </Box>
                  </Tooltip>
                </FormLabel>
                <Input
                  value={campaignInfo.optInKeywords || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCampaignInfo({
                      ...campaignInfo,
                      optInKeywords: e.target.value,
                    })
                  }
                  placeholder="Example: Subscribe, Start"
                  maxLength={255}
                />
                <FormHelperText>
                  Comma-separated list (max 255 characters)
                </FormHelperText>
              </FormControl>

              <FormControl
                isRequired={
                  !!(
                    campaignInfo.optInKeywords &&
                    campaignInfo.optInKeywords.trim()
                  )
                }
              >
                <FormLabel>
                  Opt-in Message (auto-reply)
                  {campaignInfo.optInKeywords &&
                    campaignInfo.optInKeywords.trim() && (
                      <Badge colorScheme="red" ml={2}>
                        Required
                      </Badge>
                    )}
                  <Tooltip label="Auto-reply message sent when users text an opt-in keyword">
                    <Box as="span" ml={1} cursor="help">
                      <Info size={14} style={{ display: "inline" }} />
                    </Box>
                  </Tooltip>
                </FormLabel>
                <Textarea
                  value={campaignInfo.optInMessage || ""}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setCampaignInfo({
                      ...campaignInfo,
                      optInMessage: e.target.value,
                    })
                  }
                  placeholder="Example: Acme Corporation: You are now opted-in. For help, reply HELP. To opt-out, reply STOP"
                  rows={3}
                  resize="vertical"
                />
                <FormHelperText>
                  {(campaignInfo.optInMessage || "").length} / 320 characters
                  (minimum 20 if keywords provided)
                </FormHelperText>
              </FormControl>
              {/* Optional: Opt-out & Help messages */}
              <HStack align="start">
                <FormControl>
                  <FormLabel>Opt-out Message (optional)</FormLabel>
                  <Textarea
                    value={campaignInfo.optOutMessage || ""}
                    onChange={(e) =>
                      setCampaignInfo({
                        ...campaignInfo,
                        optOutMessage: e.target.value,
                      })
                    }
                    placeholder="Reply STOP to unsubscribe. For help, reply HELP."
                    rows={2}
                    resize="vertical"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Help Message (optional)</FormLabel>
                  <Textarea
                    value={campaignInfo.helpMessage || ""}
                    onChange={(e) =>
                      setCampaignInfo({
                        ...campaignInfo,
                        helpMessage: e.target.value,
                      })
                    }
                    placeholder="For assistance, contact support@example.com."
                    rows={2}
                    resize="vertical"
                  />
                </FormControl>
              </HStack>

              <HStack justify="flex-end" mt={4}>
                <Button variant="ghost" color="black" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="primary"
                  bg="primary.800"
                  _hover={{ bg: "primary.900" }}
                  onClick={handleSubmit}
                  isLoading={loading}
                >
                  Submit Registration
                </Button>
              </HStack>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default A2PRegistrationDialog;
